import { Box, HStack, Table, Thead, Tbody, Tr, Th, Td, Text } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { SortOrder } from '@duke-hq/lib/pagination';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { StateIcon } from './StateIcon';
import { formatCurrency } from '@duke-hq/lib/numberFormats';
import type { SortFilters } from './SiteLeaderboard';

type SiteLeaderboardListProps = {
  sites?: RouterOutputs['groupRewards']['listSiteLeaderboardWithInfiniteScroll']['items'];
  filters: SortFilters;
  onFilters: (filters: SortFilters) => void;
};

const SiteLeaderboardRow: React.FC<{
  site: RouterOutputs['groupRewards']['listSiteLeaderboardWithInfiniteScroll']['items'][number];
}> = React.memo(({ site }) => {
  return (
    <Tr _hover={{ bg: 'bg.subtle' }}>
      <Td>
        <HStack spacing={2}>
          <Box
            width="28px"
            height="28px"
            borderRadius="50%"
            bg="#319795"
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="xs"
            fontWeight="bold"
          >
            {site.rank}
          </Box>
          <HStack spacing={2}>
            {site.siteState != null || site.siteState != undefined ? (
              <StateIcon siteState={site.siteState} />
            ) : null}
            <Text fontSize="xs" fontWeight="medium">
              {site.siteName}
            </Text>
          </HStack>
        </HStack>
      </Td>
      <Td>
        <HStack spacing={3} justify="flex-end">
          <Text fontSize="sm" color="green.500" fontWeight="bold">
            {formatCurrency({ number: site.totalAllocated ?? 0, maxFractionDigits: 0 })}
          </Text>
        </HStack>
      </Td>
    </Tr>
  );
});

SiteLeaderboardRow.displayName = 'SiteLeaderboardRow';

export function SiteLeaderboardTable({ sites = [], filters, onFilters }: SiteLeaderboardListProps) {
  const { sort, orderBy } = filters;

  const handleSort = useCallback(
    (orderBy: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      onFilters({ ...filters, sort: newSortDirection, orderBy });
    },
    [onFilters, sort, filters],
  );

  return (
    <Table>
      <Thead position="sticky" top={0} bg="white" zIndex={1} boxShadow="sm">
        <Tr>
          <Th>
            <Trans>Rank / Site</Trans>
          </Th>
          <Th>
            <HStack justify="flex-end">
              <SortableHeader
                label={t`Total`}
                columnName="totalSpent"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </HStack>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(sites) && sites.length
          ? sites.map((site) => <SiteLeaderboardRow key={site.siteId} site={site} />)
          : null}
      </Tbody>
    </Table>
  );
}
